/**
 * Bundle 75 Task 3 - Super-admin Consumer Audit page.
 *
 * Renders a human-readable view of the two enforcement whitelists
 * (profile-completeness + purchase-dispatch) so ops can spot
 * consumer drift without spelunking through pytest.
 *
 * No em/en dashes, no eslint-disable, no photo/image alt copy.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, Copy, ExternalLink, ShieldCheck, AlertTriangle, Filter } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

const STATUS_STYLES = {
  canonical: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  whitelisted: 'bg-amber-100 text-amber-800 border-amber-200',
  drift_detected: 'bg-red-100 text-red-800 border-red-200',
};

const STATUS_LABELS = {
  canonical: 'Canonical',
  whitelisted: 'Whitelisted',
  drift_detected: 'Drift',
};

const AuditTable = ({ section, sectionKey, filter, sortKey, setSortKey }) => {
  const rows = useMemo(() => {
    const list = filter === 'all'
      ? section.rows
      : section.rows.filter((r) => r.status === filter);
    const sorted = [...list].sort((a, b) => {
      if (sortKey === 'file') return (a.file || '').localeCompare(b.file || '');
      if (sortKey === 'last_touched') return (b.last_touched || '').localeCompare(a.last_touched || '');
      if (sortKey === 'hits') return (b.hits || 0) - (a.hits || 0);
      return 0;
    });
    return sorted;
  }, [section.rows, filter, sortKey]);

  return (
    <div className="overflow-x-auto" data-testid={`consumer-audit-table-${sectionKey}`}>
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-neutral-200 text-neutral-500 text-[10px] uppercase tracking-wider">
            <th className="text-left py-2 pr-2 cursor-pointer hover:text-primary-900" onClick={() => setSortKey('file')}>File</th>
            <th className="text-left py-2 pr-2">Status</th>
            <th className="text-left py-2 pr-2 cursor-pointer hover:text-primary-900" onClick={() => setSortKey('hits')}>Hits</th>
            <th className="text-left py-2 pr-2">Docstring</th>
            <th className="text-left py-2 pr-2 cursor-pointer hover:text-primary-900" onClick={() => setSortKey('last_touched')}>Last touched</th>
            <th className="text-left py-2 pr-2"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.file} className="border-b border-neutral-100 hover:bg-neutral-50" data-testid={`consumer-audit-row-${sectionKey}-${r.file}`}>
              <td className="py-2 pr-2 font-mono text-[11px] text-primary-900 max-w-[280px] truncate" title={r.file}>{r.file}</td>
              <td className="py-2 pr-2">
                <Badge variant="outline" className={`text-[10px] ${STATUS_STYLES[r.status] || ''}`}>
                  {STATUS_LABELS[r.status] || r.status}
                </Badge>
              </td>
              <td className="py-2 pr-2 text-neutral-700">{r.hits || 0}</td>
              <td className="py-2 pr-2 text-neutral-600 max-w-[280px] truncate" title={r.docstring}>{r.docstring || '-'}</td>
              <td className="py-2 pr-2 text-neutral-500 whitespace-nowrap">
                {r.last_touched ? new Date(r.last_touched).toLocaleDateString() : '-'}
              </td>
              <td className="py-2 pr-2">
                <button
                  type="button"
                  className="p-1 rounded hover:bg-neutral-100 text-neutral-500"
                  onClick={() => {
                    navigator.clipboard.writeText(r.file).catch(() => {});
                    toast.success(`Copied ${r.file}`);
                  }}
                  data-testid={`consumer-audit-copy-${r.file}`}
                  title="Copy path"
                >
                  <Copy className="h-3 w-3" />
                </button>
              </td>
            </tr>
          ))}
          {!rows.length ? (
            <tr>
              <td colSpan={6} className="py-6 text-center text-neutral-500 text-xs">No rows for this filter.</td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
};

const SectionCard = ({ sectionKey, section }) => {
  const [filter, setFilter] = useState('all');
  const [sortKey, setSortKey] = useState('file');
  return (
    <Card className="mb-6" data-testid={`consumer-audit-section-${sectionKey}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between flex-wrap gap-2 mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">
              {sectionKey === 'profile_completeness' ? 'Profile completeness helper' : 'Purchase CAPI dispatcher'}
            </div>
            <div className="text-sm font-semibold text-primary-900 mt-0.5 font-mono">{section.helper}</div>
            <a href={`file://${section.arch_doc}`} className="text-[11px] text-secondary-700 hover:text-secondary-800 inline-flex items-center gap-1 mt-1" data-testid={`consumer-audit-doc-${sectionKey}`}>
              <ExternalLink className="h-3 w-3" /> {section.arch_doc}
            </a>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {[
              { key: 'all', label: `All (${section.rows.length})` },
              { key: 'canonical', label: `Canonical (${section.counts.canonical || 0})`, cls: 'bg-emerald-50 text-emerald-800' },
              { key: 'whitelisted', label: `Whitelisted (${section.counts.whitelisted || 0})`, cls: 'bg-amber-50 text-amber-800' },
              { key: 'drift_detected', label: `Drift (${section.counts.drift_detected || 0})`, cls: 'bg-red-50 text-red-800' },
            ].map((chip) => (
              <button
                key={chip.key}
                type="button"
                onClick={() => setFilter(chip.key)}
                className={`text-[10px] px-2 py-1 rounded-full border transition-colors ${
                  filter === chip.key
                    ? 'border-primary-900 bg-primary-900 text-white'
                    : `${chip.cls || 'bg-neutral-50 text-neutral-700'} border-neutral-200 hover:bg-neutral-100`
                }`}
                data-testid={`consumer-audit-chip-${sectionKey}-${chip.key}`}
              >
                {chip.label}
              </button>
            ))}
          </div>
        </div>
        <AuditTable
          section={section}
          sectionKey={sectionKey}
          filter={filter}
          sortKey={sortKey}
          setSortKey={setSortKey}
        />
      </CardContent>
    </Card>
  );
};

const AdminConsumerAuditPage = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');

  const fetchAudit = async (refresh = 0) => {
    setBusy(true);
    try {
      const { data: d } = await api.get(`/admin/ops/consumer-audit?refresh=${refresh}`);
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load audit');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { fetchAudit(0); }, []);

  const filteredData = useMemo(() => {
    if (!data || !q.trim()) return data;
    const needle = q.trim().toLowerCase();
    const next = { ...data, sections: {} };
    for (const [k, s] of Object.entries(data.sections || {})) {
      next.sections[k] = {
        ...s,
        rows: s.rows.filter((r) => r.file.toLowerCase().includes(needle)),
      };
    }
    return next;
  }, [data, q]);

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="consumer-audit-page">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-secondary-800 mb-1">Ops * Enforcement</div>
          <h1 className="font-display text-3xl font-bold text-primary-900">Consumer Audit</h1>
          <p className="text-sm text-neutral-600 mt-1 max-w-2xl">
            Every file that reads a canonical helper&apos;s inputs, split by status. Green rows are the
            helper itself. Amber rows are whitelisted consumers documented in the arch doc. Red rows
            are drift and would trip the pytest guard.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {data ? (
            data.is_green ? (
              <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 inline-flex items-center gap-1 h-7" data-testid="consumer-audit-green-badge">
                <ShieldCheck className="h-3 w-3" /> Green
              </Badge>
            ) : (
              <Badge className="bg-red-100 text-red-800 border-red-200 inline-flex items-center gap-1 h-7" data-testid="consumer-audit-red-badge">
                <AlertTriangle className="h-3 w-3" /> {data.total_drift} drift
              </Badge>
            )
          ) : null}
          <Button
            size="sm" variant="outline" onClick={() => fetchAudit(1)} disabled={busy}
            data-testid="consumer-audit-refresh-btn"
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${busy ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      <div className="mb-4 flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Filter className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-neutral-400" />
          <Input
            placeholder="Filter by file path..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-8 h-8 text-sm"
            data-testid="consumer-audit-filter-input"
          />
        </div>
        {data ? (
          <div className="text-xs text-neutral-500">
            Generated {data.generated_at ? new Date(data.generated_at).toLocaleTimeString() : '-'}
          </div>
        ) : null}
      </div>

      {!filteredData ? (
        <div className="text-sm text-neutral-500">Loading audit...</div>
      ) : (
        Object.entries(filteredData.sections || {}).map(([k, s]) => (
          <SectionCard key={k} sectionKey={k} section={s} />
        ))
      )}
    </div>
  );
};

export default AdminConsumerAuditPage;
